import { useEffect, useRef, useState } from 'react';

/**
 * Counter Animation Hook
 * Animates numbers from 0 to target value
 * Professional and smooth for financial stats
 */
export const useCounterAnimation = (end, duration = 2000, startDelay = 0) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          setIsVisible(true);
          hasAnimated.current = true;
        }
      },
      { threshold: 0.5 }
    );

    const currentElement = elementRef.current;
    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const startTime = Date.now() + startDelay;
    const endValue = typeof end === 'string' ? parseInt(end.replace(/\D/g, '')) : end;

    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTime;

      if (elapsed < 0) {
        requestAnimationFrame(animate);
        return;
      }

      const progress = Math.min(elapsed / duration, 1);
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = Math.floor(easeOutQuart * endValue);

      setCount(currentCount);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(endValue);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, end, duration, startDelay]);

  return [elementRef, count];
};

/**
 * Counter component with formatted display
 */
export const AnimatedCounter = ({ 
  value, 
  suffix = '', 
  prefix = '', 
  duration = 2000,
  className = '' 
}) => {
  const numericValue = typeof value === 'string' ? parseInt(value.replace(/\D/g, '')) : value;
  const [ref, count] = useCounterAnimation(numericValue, duration);

  const formatNumber = (num) => {
    return num.toLocaleString('en-IN');
  };

  return (
    <span ref={ref} className={className}>
      {prefix}{formatNumber(count)}{suffix}
    </span>
  );
};
